/**
 * Bug Reports Admin Component
 *
 * Displays and manages bug reports submitted by users.
 * Shows list view with status/category filters, detail view with screenshot,
 * and simple analytics dashboard.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Tooltip,
    Grid,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    BugReport as BugIcon,
    Visibility as ViewIcon,
    Close as CloseIcon,
    CheckCircle as FixedIcon,
    Cancel as CancelIcon,
    Terminal as TerminalIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';

// Status color scheme
const STATUS_COLORS = {
    new: { bg: '#6b7280', label: 'New' },
    analyzing: { bg: '#3b82f6', label: 'Analyzing' },
    awaiting_review: { bg: '#f59e0b', label: 'Awaiting Review' },
    in_conversation: { bg: '#8b5cf6', label: 'In Conversation' },
    fixed: { bg: '#10b981', label: 'Fixed' },
    closed: { bg: '#6b7280', label: 'Closed' }
};

// Category labels
const CATEGORY_LABELS = {
    ui: 'UI / Display',
    calculation: 'Calculation',
    performance: 'Performance',
    data: 'Data',
    other: 'Other'
};

function BugReportsAdmin() {
    const { currentUser } = useAuth();
    const [bugReports, setBugReports] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [selectedBug, setSelectedBug] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [copySnackbar, setCopySnackbar] = useState(false);

    const copyForClaudeCode = (bug) => {
        const prompt = `I'm debugging a bug report from our Site DNA application.

**Bug Report ID:** ${bug.id}
**Category:** ${CATEGORY_LABELS[bug.category] || bug.category}
**Status:** ${STATUS_COLORS[bug.status]?.label || bug.status}
**Submitted by:** ${bug.submitted_by?.email || 'Unknown'}
**Date:** ${bug.submitted_at || 'Unknown'}

**URL where bug occurred:**
${bug.metadata?.url || 'Not provided'}

**Description:**
${bug.description}

**Screenshot:** ${bug.screenshot_url || 'Not available'}

---

Please use the /superpowers:systematic-debugging skill to:
1. Understand and reproduce the bug based on the description
2. Search the codebase to identify the root cause
3. Propose a fix with specific file changes
4. Rate your confidence (high/medium/low)

Start by exploring the relevant code based on the URL and description.`;

        navigator.clipboard.writeText(prompt).then(() => {
            setCopySnackbar(true);
        });
    };

    const fetchBugReports = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            let url = `${process.env.REACT_APP_API_URL || ''}/api/v1/bug-reports/admin?limit=50`;
            if (statusFilter) url += `&status=${statusFilter}`;
            if (categoryFilter) url += `&category=${categoryFilter}`;

            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch bug reports: ${response.status}`);
            }

            const data = await response.json();
            setBugReports(data);
        } catch (err) {
            console.error('Error fetching bug reports:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser, statusFilter, categoryFilter]);

    const fetchAnalytics = useCallback(async () => {
        if (!currentUser) return;
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/api/v1/bug-reports/admin/analytics`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const data = await response.json();
                setAnalytics(data);
            }
        } catch (err) {
            console.error('Error fetching analytics:', err);
        }
    }, [currentUser]);

    const fetchBugDetail = async (bugId) => {
        if (!currentUser) return;
        setDetailLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/api/v1/bug-reports/admin/${bugId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const data = await response.json();
                setSelectedBug(data);
            }
        } catch (err) {
            console.error('Error fetching bug detail:', err);
        } finally {
            setDetailLoading(false);
        }
    };

    const updateBugStatus = async (bugId, newStatus) => {
        if (!currentUser) return;
        setUpdating(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/api/v1/bug-reports/admin/${bugId}/status`,
                {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ status: newStatus })
                }
            );

            if (response.ok) {
                // Close dialog and refresh list/analytics
                setSelectedBug(null);
                fetchBugReports();
                fetchAnalytics();
            }
        } catch (err) {
            console.error('Error updating bug status:', err);
        } finally {
            setUpdating(false);
        }
    };

    useEffect(() => {
        fetchBugReports();
        fetchAnalytics();
    }, [fetchBugReports, fetchAnalytics]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const truncateText = (text, maxLength = 60) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <Box>
            {/* Analytics Cards */}
            {analytics && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card sx={{ bgcolor: vortxColors.charcoal }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Typography variant="h4" sx={{ color: vortxColors.gold, fontWeight: 700 }}>
                                    {analytics.total}
                                </Typography>
                                <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                    Total
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card sx={{ bgcolor: vortxColors.charcoal }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                                    {analytics.by_status.new + analytics.by_status.analyzing + analytics.by_status.awaiting_review}
                                </Typography>
                                <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                    Open
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card sx={{ bgcolor: vortxColors.charcoal }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 700 }}>
                                    {analytics.by_status.fixed}
                                </Typography>
                                <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                    Fixed
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={12} md={6}>
                        <Card sx={{ bgcolor: vortxColors.charcoal }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                        By Category:
                                    </Typography>
                                    {Object.entries(analytics.by_category)
                                        .filter(([, count]) => count > 0)
                                        .map(([cat, count]) => (
                                            <Chip
                                                key={cat}
                                                label={`${CATEGORY_LABELS[cat] || cat}: ${count}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha(vortxColors.gold, 0.15),
                                                    color: vortxColors.gold,
                                                    fontWeight: 500
                                                }}
                                            />
                                        ))}
                                    {Object.values(analytics.by_category).every(c => c === 0) && (
                                        <Typography variant="body2" sx={{ color: vortxColors.stone, fontStyle: 'italic' }}>
                                            No bugs yet
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ color: vortxColors.stone }}>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ color: vortxColors.white, '.MuiOutlinedInput-notchedOutline': { borderColor: vortxColors.stone } }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(STATUS_COLORS).map(([key, { label }]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ color: vortxColors.stone }}>Category</InputLabel>
                    <Select
                        value={categoryFilter}
                        label="Category"
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        sx={{ color: vortxColors.white, '.MuiOutlinedInput-notchedOutline': { borderColor: vortxColors.stone } }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Tooltip title="Refresh">
                    <IconButton onClick={() => { fetchBugReports(); fetchAnalytics(); }} sx={{ color: vortxColors.gold }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {/* Loading State */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: vortxColors.gold }} />
                </Box>
            ) : bugReports.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <BugIcon sx={{ fontSize: 48, color: vortxColors.stone, mb: 1 }} />
                    <Typography sx={{ color: vortxColors.stone }}>
                        No bug reports found
                    </Typography>
                </Box>
            ) : (
                /* Bug Reports Table */
                <TableContainer component={Paper} sx={{ bgcolor: vortxColors.charcoal }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Status</TableCell>
                                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Category</TableCell>
                                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Description</TableCell>
                                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Submitted By</TableCell>
                                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Date</TableCell>
                                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {bugReports.map((bug) => (
                                <TableRow
                                    key={bug.id}
                                    sx={{
                                        '&:hover': { bgcolor: alpha(vortxColors.gold, 0.05) },
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => fetchBugDetail(bug.id)}
                                >
                                    <TableCell>
                                        <Chip
                                            label={STATUS_COLORS[bug.status]?.label || bug.status}
                                            size="small"
                                            sx={{
                                                bgcolor: STATUS_COLORS[bug.status]?.bg || '#6b7280',
                                                color: '#fff',
                                                fontWeight: 500
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: vortxColors.stone }}>
                                        {CATEGORY_LABELS[bug.category] || bug.category}
                                    </TableCell>
                                    <TableCell sx={{ color: vortxColors.white }}>
                                        {truncateText(bug.description)}
                                    </TableCell>
                                    <TableCell sx={{ color: vortxColors.stone }}>
                                        {bug.submitted_by?.email || '-'}
                                    </TableCell>
                                    <TableCell sx={{ color: vortxColors.stone }}>
                                        {formatDate(bug.submitted_at)}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                sx={{ color: vortxColors.gold }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    fetchBugDetail(bug.id);
                                                }}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Bug Detail Dialog */}
            <Dialog
                open={!!selectedBug}
                onClose={() => setSelectedBug(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: vortxColors.obsidian,
                        color: vortxColors.white
                    }
                }}
            >
                {selectedBug && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <BugIcon sx={{ color: vortxColors.gold }} />
                                <Typography variant="h6">Bug Report</Typography>
                                <Chip
                                    label={STATUS_COLORS[selectedBug.status]?.label || selectedBug.status}
                                    size="small"
                                    sx={{
                                        bgcolor: STATUS_COLORS[selectedBug.status]?.bg || '#6b7280',
                                        color: '#fff'
                                    }}
                                />
                            </Box>
                            <IconButton onClick={() => setSelectedBug(null)} sx={{ color: vortxColors.stone }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            {detailLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress sx={{ color: vortxColors.gold }} />
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    {/* Left Column - Info */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" sx={{ color: vortxColors.stone, mb: 0.5 }}>
                                            Category
                                        </Typography>
                                        <Typography sx={{ mb: 2 }}>
                                            {CATEGORY_LABELS[selectedBug.category] || selectedBug.category}
                                        </Typography>

                                        <Typography variant="subtitle2" sx={{ color: vortxColors.stone, mb: 0.5 }}>
                                            Description
                                        </Typography>
                                        <Typography sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                                            {selectedBug.description}
                                        </Typography>

                                        <Typography variant="subtitle2" sx={{ color: vortxColors.stone, mb: 0.5 }}>
                                            Submitted By
                                        </Typography>
                                        <Typography sx={{ mb: 2 }}>
                                            {selectedBug.submitted_by?.email || '-'}
                                        </Typography>

                                        <Typography variant="subtitle2" sx={{ color: vortxColors.stone, mb: 0.5 }}>
                                            Date
                                        </Typography>
                                        <Typography sx={{ mb: 2 }}>
                                            {formatDate(selectedBug.submitted_at)}
                                        </Typography>

                                        {selectedBug.metadata?.url && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ color: vortxColors.stone, mb: 0.5 }}>
                                                    URL
                                                </Typography>
                                                <Typography sx={{ mb: 2, wordBreak: 'break-all' }}>
                                                    {selectedBug.metadata.url}
                                                </Typography>
                                            </>
                                        )}
                                    </Grid>

                                    {/* Right Column - Screenshot */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" sx={{ color: vortxColors.stone, mb: 1 }}>
                                            Screenshot
                                        </Typography>
                                        {selectedBug.screenshot_url ? (
                                            <Box
                                                component="img"
                                                src={selectedBug.screenshot_url}
                                                alt="Bug screenshot"
                                                sx={{
                                                    width: '100%',
                                                    maxHeight: 400,
                                                    objectFit: 'contain',
                                                    borderRadius: 1,
                                                    border: `1px solid ${vortxColors.charcoal}`,
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => window.open(selectedBug.screenshot_url, '_blank')}
                                            />
                                        ) : (
                                            <Typography sx={{ color: vortxColors.stone }}>
                                                No screenshot available
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                startIcon={<TerminalIcon />}
                                onClick={() => copyForClaudeCode(selectedBug)}
                                sx={{
                                    bgcolor: vortxColors.gold,
                                    color: vortxColors.obsidian,
                                    '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) }
                                }}
                            >
                                Copy for Claude Code
                            </Button>
                            {selectedBug.status !== 'fixed' && selectedBug.status !== 'closed' && (
                                <>
                                    <Button
                                        variant="contained"
                                        startIcon={<FixedIcon />}
                                        onClick={() => updateBugStatus(selectedBug.id, 'fixed')}
                                        disabled={updating}
                                        sx={{
                                            bgcolor: '#10b981',
                                            '&:hover': { bgcolor: '#059669' }
                                        }}
                                    >
                                        Mark Fixed
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={() => updateBugStatus(selectedBug.id, 'closed')}
                                        disabled={updating}
                                        sx={{
                                            color: vortxColors.stone,
                                            borderColor: vortxColors.stone
                                        }}
                                    >
                                        Close
                                    </Button>
                                </>
                            )}
                            <Button
                                onClick={() => setSelectedBug(null)}
                                sx={{ color: vortxColors.gold }}
                            >
                                Done
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Copy confirmation snackbar */}
            <Snackbar
                open={copySnackbar}
                autoHideDuration={3000}
                onClose={() => setCopySnackbar(false)}
                message="Bug info copied! Paste into Claude Code to start debugging."
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}

export default BugReportsAdmin;
